import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';

import { AspectRatioWrapper } from '../../components';

import css from './ImageFromFile.module.css';

// Resolved when FileReader.abort() runs (intentional unmount / remove).
const ABORTED = { aborted: true };

/**
 * Read a File as a data URL. Call abort() on the returned handle to cancel silently.
 *
 * @param {File} file
 * @returns {{ promise: Promise<string|{aborted: true}>, abort: Function }}
 */
const readImage = file => {
  let reader = null;

  const promise = new Promise((resolve, reject) => {
    if (!window?.FileReader) {
      reject(new Error(`No FileReader found from window scope.`));
      return;
    }
    reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onabort = () => resolve(ABORTED);
    reader.onerror = e => {
      // Some environments surface abort as an error event; treat it as cancel.
      if (reader.error?.name === 'AbortError') {
        resolve(ABORTED);
        return;
      }
      console.error('Error (', e, `) happened while reading ${file.name}: ${e.target.result}`);
      reject(new Error(`Error reading ${file.name}: ${e.target.result}`));
    };
    reader.readAsDataURL(file);
  });

  return {
    promise,
    abort: () => {
      if (reader && reader.readyState === FileReader.LOADING) {
        reader.abort();
      }
    },
  };
};

/**
 * Create image component from the given image file.
 * Note: currently this relies on dataURL.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id
 * @param {number?} props.aspectWidth
 * @param {number?} props.aspectHeight
 * @param {File?} props.file from <input type="file" />
 * @param {Function?} props.onError called with the File on a genuine read failure (not abort)
 * @param {ReactNode?} props.children
 * @returns {JSX.Element}
 */
const ImageFromFile = props => {
  const {
    className,
    rootClassName,
    aspectWidth = 1,
    aspectHeight = 1,
    file,
    id,
    children,
    onError,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Stabilize effect deps: UUID objects from the API can change identity across renders.
  const idKey = typeof id === 'string' ? id : id?.uuid || String(id ?? '');

  const [dataURL, setDataURL] = useState(null);
  const [readFailed, setReadFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setDataURL(null);
    setReadFailed(false);
    const { promise, abort } = readImage(file);

    // Attach handlers in the same turn as promise creation so a fast failure
    // cannot surface as an unhandledrejection / webpack overlay.
    promise
      .then(result => {
        if (!alive) {
          return;
        }
        if (result === ABORTED) {
          return;
        }
        setDataURL(result);
      })
      .catch(err => {
        if (!alive) {
          return;
        }
        setReadFailed(true);
        if (typeof onErrorRef.current === 'function') {
          onErrorRef.current(file, err);
        }
      });

    return () => {
      alive = false;
      abort();
    };
  }, [file, idKey]);

  if (readFailed) {
    return (
      <div className={classes}>
        <FormattedMessage id="ImageFromFile.couldNotReadFile" />
      </div>
    );
  }

  if (!dataURL) {
    // Still reading (or aborted while unmounting) — keep children (spinner / remove).
    return (
      <div className={classes}>
        <AspectRatioWrapper width={aspectWidth} height={aspectHeight} />
        {children}
      </div>
    );
  }

  return (
    <div className={classes}>
      <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
        <img src={dataURL} alt={file.name} className={css.rootForImage} />
      </AspectRatioWrapper>
      {children}
    </div>
  );
};

export default ImageFromFile;
